import { Formik } from 'formik';
import { createContext, useState } from 'react';
import * as XLSX from 'xlsx';
import { ValidationError } from 'yup';

import { CustomerImportCSVEditBillingAddress } from './CustomerImportCSVEditBillingAddress';
import { CustomerImportCSVEditContact } from './CustomerImportCSVEditContact';
import { CustomerImportCSVEditShippingAddress } from './CustomerImportCSVEditShippingAddress';

import { CloseButton } from '@components/ActionButtons';
import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  DetailTableDataLeft,
  DetailTableHeaderLeft,
} from '@components/Details';
import {
  CancelButton,
  FormikCheckbox,
  FormikInput,
  FormikTextArea,
  SubmitButton,
} from '@components/Form';
import { CsvFileInput } from '@components/Form/CsvFileInput';
import { TableActions } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { Table, TableContainer } from '@components/TableContainers';
import { useNotification } from '@hooks/useNotification';
import { BillingAddressViewModel } from '@models/BillingAddress';
import { ContactViewModel } from '@models/Contact';
import { customerDefaultValue, CustomerViewModel } from '@models/Customer';
import { csvCustomerValidationSchema } from '@models/ImportCSV';
import { ShippingAddressViewModel } from '@models/ShippingAddress';
import { CustomerService } from '@services/Customer';

interface ICustomerCsvContextModel {
  customer: CustomerViewModel | null;
  setCustomer: React.Dispatch<React.SetStateAction<CustomerViewModel | null>>;
  updateCustomerInCsv: (updated: CustomerViewModel) => void;
}

export const CustomerEditCsvContext = createContext<ICustomerCsvContextModel>({
  customer: customerDefaultValue,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCustomer: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateCustomerInCsv: () => {},
});

interface Props {
  onClose?: () => void;
}

export const CustomerImportCSV: React.FC<Props> = ({ onClose }) => {
  const [csvData, setCsvData] = useState<CustomerViewModel[]>([]);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isCsvValid, setIsCsvValid] = useState<boolean>(true);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerViewModel | null>(null);
  const { showSuccess, showError } = useNotification();

  const validateCsvData = async (data: CustomerViewModel[]) => {
    const errors: { index: number; message: string }[] = [];

    for (let i = 0; i < data.length; i++) {
      try {
        await csvCustomerValidationSchema.validate(data[i], {
          abortEarly: false,
        });
      } catch (err) {
        if (err instanceof ValidationError) {
          errors.push({
            index: i + 1,
            message: err.errors.join(', '),
          });
        }
      }
    }

    return errors;
  };

  const handleCsvUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = e.target?.result;
        if (data) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsedData: CustomerViewModel[] = XLSX.utils
            .sheet_to_json(worksheet)
            .map((row: any, idx: number) => {
              const shippingFields = {
                currencyCode: `${row['Shipping Currency Code'] || ''}`,
                address1: `${row['Shipping Address 1'] || ''}`,
                address2: `${row['Shipping Address 2'] || ''}`,
                city: `${row['Shipping Address City'] || ''}`,
                state: `${row['Shipping Address State'] || ''}`,
                country: `${row['Shipping Address Country'] || ''}`,
                postalCode: `${row['Shipping Address Postal Code'] || ''}`,
                isActive: true as const,
              };

              const isShippingEmpty = Object.values(shippingFields).every(
                (val) => val === '' || val === true
              );

              // Generate a unique id and uiid for each customer
              const id = row['ID'] ?? `csv-${idx + 1}`;
              const uiid = row['UIID'] ?? `csv-uiid-${idx + 1}`;

              return {
                id,
                uiid,
                companyName: row['Company Name'],
                accountNumber: `${row['Account Number']}`,
                website: row['Website'],
                notes: row['Notes'],
                contactID: row['Contact ID'],
                primaryContact: {
                  id: row['Primary Contact ID'] ?? `contact-${idx + 1}`,
                  uiid:
                    row['Primary Contact UIID'] ?? `contact-uiid-${idx + 1}`,
                  data: {},
                  firstname: `${row['Primary Contact First Name']}`,
                  lastname: `${row['Primary Contact Last Name']}`,
                  email: `${row['Primary Contact Email']}`,
                  phone: `${row['Primary Contact Phone']}`,
                  fax: `${row['Primary Contact Fax']}`,
                  isActive: true,
                },
                defaultBillingAddress: {
                  id: row['Billing Address ID'] ?? `billing-${idx + 1}`,
                  uiid:
                    row['Billing Address UIID'] ?? `billing-uiid-${idx + 1}`,
                  data: {},
                  currencyCode: `${row['Billing Currency Code']}`,
                  address1: `${row['Billing Address 1']}`,
                  address2: `${row['Billing Address 2']}`,
                  city: `${row['Billing Address City']}`,
                  state: `${row['Billing Address State']}`,
                  country: `${row['Billing Address Country']}`,
                  postalCode: `${row['Billing Address Postal Code']}`,
                  isActive: true,
                },
                ...(isShippingEmpty
                  ? {
                      isSameShipping: true,
                      shippingId: null,
                      defaultShippingAddress: undefined,
                    }
                  : {
                      isSameShipping: false,
                      defaultShippingAddress: {
                        id: row['Shipping Address ID'] ?? `shipping-${idx + 1}`,
                        uiid:
                          row['Shipping Address UIID'] ??
                          `shipping-uiid-${idx + 1}`,
                        data: {},
                        ...shippingFields,
                        isActive: true as const,
                      },
                    }),
              };
            });
          setCsvData(parsedData);
          setIsUploadComplete(true);

          const validationErrors = await validateCsvData(parsedData);
          setIsCsvValid(validationErrors.length === 0);
          console.log('ParsedData:', parsedData);
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleCsvDataSubmit = async () => {
    const validationErrors = await validateCsvData(csvData);
    if (validationErrors.length > 0) {
      setIsCsvValid(false);
      showError('CSV Validation Errors');
      console.error('CSV Validation Errors:', validationErrors);
      return;
    }
    try {
      const batchSize = 10;
      const totalCustomers = csvData.length;
      let startIndex = 0;

      while (startIndex < totalCustomers) {
        const endIndex = Math.min(startIndex + batchSize, totalCustomers);
        const batch = csvData.slice(startIndex, endIndex);

        const createPromises = batch.map(async (customer) => {
          return CustomerService.create(customer);
        });

        await Promise.all(createPromises);

        startIndex += batchSize;
      }
      console.log('All submissions successful');
      window.location.reload();
      setCsvData([]);
    } catch (error) {
      console.log('Submission failed: error');
      alert(error);
    }
  };
  const handleViewDetails = (customer: CustomerViewModel) => {
    setSelectedCustomer(customer);
  };

  const handleCloseView = () => {
    setSelectedCustomer(null);
  };

  const formatAddress = (
    addr?: BillingAddressViewModel | ShippingAddressViewModel
  ) => {
    if (!addr) return '';
    return `${addr.address1 || ''} ${addr.address2 || ''}, ${
      addr.city || ''
    }, ${addr.state || ''}, ${addr.country || ''} ${addr.postalCode || ''}`;
  };

  const fullName = (contact?: ContactViewModel) => {
    if (!contact) return '';
    return `${contact.firstname || ''} ${contact.lastname || ''}`;
  };

  return (
    <>
      <CloseButton onClick={onClose} />
      <Box size="medium">
        <FormSectionContainer>
          {!isUploadComplete && (
            <CsvFileInput
              onChange={handleCsvUpload}
              label="Import Data from Excel / CSV"
              name="csvFile"
            />
          )}
          {isUploadComplete && (
            <>
              <div>Import Data from Excel / CSV</div>
              <div className="bg-gray-100 rounded-md p-2 text-sm text-gray-500 italic">
                Please ensure all required fields are filled.
                <br />
                Incomplete customer data cannot be imported.
              </div>
              <TableContainer>
                <Table>
                  <thead className="br-primary-100">
                    <tr>
                      <DetailTableHeaderLeft>
                        <span className="whitespace-nowrap">Company Name</span>
                      </DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>
                        <span className="whitespace-nowrap">
                          Account Number
                        </span>
                      </DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>Website</DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>
                        Contact Person
                      </DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>Email</DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>Phone</DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>Fax</DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>
                        Billing Address
                      </DetailTableHeaderLeft>
                      <DetailTableHeaderLeft>
                        Shipping Address
                      </DetailTableHeaderLeft>
                      <DetailTableHeaderLeft></DetailTableHeaderLeft>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((data, index) => (
                      <tr
                        key={index}
                        className={`${
                          index % 2 === 0
                            ? 'bg-white'
                            : 'bg-primary-500 bg-opacity-25'
                        }`}
                      >
                        <DetailTableDataLeft>
                          {data.companyName || (
                            <span className="text-red-500 italic">
                              Please provide a company name
                            </span>
                          )}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.accountNumber}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.website}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.primaryContact?.firstname}{' '}
                          {data.primaryContact?.lastname}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.primaryContact?.email}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.primaryContact?.phone}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.primaryContact?.fax}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.defaultBillingAddress?.address1}{' '}
                          {data.defaultBillingAddress?.address2}
                          <br />
                          {data.defaultBillingAddress?.city}{' '}
                          {data.defaultBillingAddress?.state}{' '}
                          {data.defaultBillingAddress?.country},{' '}
                          {data.defaultBillingAddress?.postalCode}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          {data.isSameShipping ? (
                            <div className="text-sm italic">
                              same as Billing Address
                            </div>
                          ) : (
                            <>
                              {data.defaultShippingAddress?.address1}{' '}
                              {data.defaultShippingAddress?.address2}
                              <br />
                              {data.defaultShippingAddress?.city}{' '}
                              {data.defaultShippingAddress?.state}{' '}
                              {data.defaultShippingAddress?.country},{' '}
                              {data.defaultShippingAddress?.postalCode}
                            </>
                          )}
                        </DetailTableDataLeft>
                        <DetailTableDataLeft>
                          <TableActions
                            inlineActions={[
                              {
                                title: 'Edit',
                                onClick: () => handleViewDetails(data),
                              },
                            ]}
                          />
                        </DetailTableDataLeft>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>

              <div className="text-xs">
                <button
                  onClick={() => {
                    setIsUploadComplete(false);
                    setCsvData([]);
                  }}
                  className="italic py-2 text-blue-500 hover:text-blue-800"
                >
                  Try Again
                </button>
              </div>
            </>
          )}

          <Space />
        </FormSectionContainer>
        <FormButtonsContainer>
          <CancelButton onClick={onClose} />

          <SubmitButton
            label="Finish"
            onClick={handleCsvDataSubmit}
            disabled={!isCsvValid || csvData.length === 0}
          />
        </FormButtonsContainer>
      </Box>

      {selectedCustomer && (
        <CustomerEditCsvContext.Provider
          value={{
            customer: selectedCustomer,
            setCustomer: setSelectedCustomer,
            updateCustomerInCsv: async (updated) => {
              setCsvData((prev) =>
                prev.map((c) => (c === selectedCustomer ? updated : c))
              );

              const updatedCsv = csvData.map((c) =>
                c === selectedCustomer ? updated : c
              );
              const validationErrors = await validateCsvData(updatedCsv);
              setIsCsvValid(validationErrors.length === 0);
            },
          }}
        >
          <Modal show={true}>
            <CustomerEditCsvContext.Consumer>
              {({ updateCustomerInCsv }) => (
                <div className="space-y-4">
                  <div className="flex lg:text-base md:text-base text-sm lg:pl-4 md:pl-4 pl-2 justify-between items-center">
                    <div className="w-full">Customer Details</div>
                    <div>
                      <CloseButton onClick={handleCloseView} />
                    </div>
                  </div>

                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      ...selectedCustomer,
                      fullBillingAddress: formatAddress(
                        selectedCustomer.defaultBillingAddress
                      ),
                      fullShippingAddress: formatAddress(
                        selectedCustomer.defaultShippingAddress
                      ),
                      contactName: fullName(selectedCustomer.primaryContact),
                    }}
                    validationSchema={csvCustomerValidationSchema}
                    validateOnBlur={true}
                    validateOnChange={true}
                    onSubmit={async (values, actions) => {
                      if (!selectedCustomer) return;

                      const updatedCustomer = {
                        ...selectedCustomer,
                        ...values,
                        primaryContact: selectedCustomer.primaryContact,
                        defaultBillingAddress:
                          selectedCustomer.defaultBillingAddress,
                        defaultShippingAddress:
                          selectedCustomer.defaultShippingAddress,
                      };

                      await updateCustomerInCsv(updatedCustomer);

                      handleCloseView();
                      showSuccess(
                        'Customer updated locally. Changes will be saved on Finish.'
                      );

                      actions.setSubmitting(false);
                    }}
                  >
                    {(formikProps) => {
                      return (
                        <>
                          <form
                            method="POST"
                            onSubmit={formikProps.handleSubmit}
                          >
                            <FormSectionContainer>
                              <FormikInput
                                label="Name / Company Name"
                                name="companyName"
                              />
                              <FormikInput
                                label="Account Number"
                                name="accountNumber"
                              />
                              <FormikInput label="Website" name="website" />

                              <CustomerImportCSVEditContact />
                              <BoxRowColumn>
                                <BoxColumn>
                                  <FormikInput
                                    label="Email"
                                    name="primaryContact.email"
                                    readonly={true}
                                  />
                                </BoxColumn>
                                <BoxColumn>
                                  <FormikInput
                                    label="Phone"
                                    name="primaryContact.phone"
                                    readonly={true}
                                  />
                                </BoxColumn>
                              </BoxRowColumn>

                              <CustomerImportCSVEditBillingAddress />

                              <FormikCheckbox
                                label="Same as Billing Address"
                                name="isSameShipping"
                              />

                              {!formikProps.values.isSameShipping && (
                                <CustomerImportCSVEditShippingAddress />
                              )}

                              <FormikTextArea label="Notes" name="notes" />

                              <FormikCheckbox
                                label="Tax Exempt"
                                name="isTaxExempt"
                              />

                              <Space />
                            </FormSectionContainer>
                            <FormButtonsContainer>
                              <CancelButton
                                onClick={() => {
                                  handleCloseView();
                                }}
                              />

                              <SubmitButton
                                label="Save"
                                disabled={
                                  formikProps.isSubmitting ||
                                  !formikProps.isValid
                                }
                              />
                            </FormButtonsContainer>
                          </form>
                        </>
                      );
                    }}
                  </Formik>
                </div>
              )}
            </CustomerEditCsvContext.Consumer>
          </Modal>
        </CustomerEditCsvContext.Provider>
      )}
    </>
  );
};
